import { isApiError } from "@core/api/api-error";
import { useI18n } from "@core/i18n/i18n";
import { useQueryParams } from "@core/routing/use-query-params";
import { useStateParams } from "@core/routing/use-state-params";
import { libraryFilterManager } from "@core/service-container";
import { getEmptyNewTemplate } from "@domain/template/template";
import styled from "@emotion/styled";
import { NetworkError } from "@ui/components/common/error";
import { LoadingContainer } from "@ui/components/common/loading-container";
import { RoutesPath } from "@ui/navigation/routes";
import { TemplateBuilderView } from "@ui/pages/template-builder/components/template-builder-view";
import { useTemplate } from "@ui/pages/template-builder/hooks/use-templates";
import { stack } from "@ui/style/layout";
import React, { useEffect, useMemo } from "react";

const TemplateBuilder: React.FC = () => {
	const { templateId } = useQueryParams<RoutesPath.TemplateBuilder>();
	const { format } = useI18n();
	const stateParams = useStateParams<RoutesPath.TemplateBuilder>();

	const { loading, template, error, refreshTemplate } = useTemplate(templateId);

	const currentTemplate = useMemo(() => (templateId ? template : getEmptyNewTemplate()), [template, templateId]);

	useEffect(() => {
		libraryFilterManager.clearAllSelectedExercises();
		libraryFilterManager.selectExercises(stateParams.preSelectedExercises ?? []);
		return () => {
			libraryFilterManager.clearAllSelectedExercises();
		};
	}, [stateParams.preSelectedExercises]);

	if (!currentTemplate) {
		if (loading) {
			return <LoadingContainer />;
		}
		return (
			<Container>
				<NetworkError
					description={isApiError(error) ? format("templateBuilder.templateNotFoundError") : undefined}
					onRefresh={async () => {
						if (templateId) {
							await refreshTemplate(templateId);
						}
					}}
				/>
			</Container>
		);
	}

	return <TemplateBuilderView template={currentTemplate} templateIsLoading={loading} />;
};

export default TemplateBuilder;

const Container = styled.div`
	${stack(undefined, "center", "stretch")};
	flex-grow: 1;
`;
